import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { useTheme } from '@emotion/react'

import { numberToDollars, formatPercentLabel } from '../../utils/utils'
import { buildArcPath, tickMarkPoints, pointOnGauge, END_ANGLE } from './arcGeometry'
import useAnimatedPercent from './useAnimatedPercent'

const FONT_FAMILY = "'Open Sans', 'Helvetica Neue', sans-serif"

export const PRIMARY_RADIUS = 105 // single-mode ring; dual-mode inner ring
const RING_THICKNESS = 14
const PADDING = 10

// Bottom viewBox clearance for optional captions.
const CAPTION_CLEARANCE = 15
const CAPTION_CLEARANCE_WITH_SUBLABEL = 25

// Dual-mode geometry.
const RING_GAP = 10
const OUTER_RADIUS = PRIMARY_RADIUS + RING_GAP + RING_THICKNESS
const OUTER_TRACK_TRIM = 1 // slightly shortens the outer ring so it reads lighter
const TICK_OUTER_OVERSHOOT = 7 // extends the recommended tick past the outer ring
const TICK_WIDTH = 2

const AMOUNT_X = -3 // nudge left so the amount, including the dollar sign, looks centered
const SUB_LABEL_FONT_WEIGHT = 700

export const PLAN_BLUE = '#4fadcc'
export const GOAL_BLUE = '#d4ebf2'
export const DOLLAR_SIGN_GREEN = '#4d9927'

// Explicit overrides take precedence; otherwise use theme colors, with local
// fallbacks where defined.
const resolveColors = (theme, overrides) => ({
  track: theme.ternaryColor,
  goal: theme.primaryLighter || GOAL_BLUE,
  plan: theme.donutPlanColor || PLAN_BLUE,
  digits: theme.darkestGray,
  dollarSign: DOLLAR_SIGN_GREEN,
  monthly: theme.lightestGray,
  subLabel: theme.primaryColor,
  goalCallout: theme.mediumGray,
  tick: theme.darkestGray,
  ...overrides,
})

// Positions relative to the donut center (0, 0); negative y sits above it.
const LAYOUT_DEFAULTS = {
  radius: PRIMARY_RADIUS,
  ringThickness: RING_THICKNESS,
  amountOffsetY: -28,
  amountFontSize: 26,
  amountFontWeight: 600,
  monthlyOffsetY: -7,
  monthlyFontSize: 14,
  monthlyFontWeight: 300,
  subLabelOffsetY: 14,
  subLabelFontSize: 10,
  calloutGap: 14,
  padding: PADDING,
}
const resolveLayout = overrides => ({ ...LAYOUT_DEFAULTS, ...overrides })

// Account for the extended arc and rounded caps dipping below y=0.
const arcDip = (radius, ringThickness = RING_THICKNESS) =>
  -Math.cos(END_ANGLE) * radius + ringThickness / 2

// Leave extra space around the arc in the SVG viewBox.
export const chartWidth = (radius, padding = PADDING) => 2 * (radius + padding)

const chartHeight = (radius, captionClearance, ringThickness = RING_THICKNESS, padding = PADDING) =>
  radius + padding + Math.max(captionClearance, arcDip(radius, ringThickness) + padding)

// Exported default dimensions for parent layouts to size the charts.
export const CHART_WIDTH_SINGLE = chartWidth(PRIMARY_RADIUS)
export const CHART_WIDTH_DUAL = chartWidth(OUTER_RADIUS)
export const CHART_HEIGHT_DUAL = chartHeight(OUTER_RADIUS, CAPTION_CLEARANCE, RING_THICKNESS)

export const ARC_CAPTION_LAYOUT = {
  subLabelFontSize: 18,
  subLabelOffsetY: 33,
}

export const incomeGoalLayout = (compact = false) => ({
  amountFontSize: 30,
  amountFontWeight: 700,
  monthlyFontWeight: 700,
  amountOffsetY: compact ? -27 : -21,
  monthlyOffsetY: compact ? -4 : 2,
})

// Reserve enough bottom clearance for the larger caption while keeping both
// chart variants on the same baseline.
const ARC_CAPTION_CLEARANCE = Math.max(
  CAPTION_CLEARANCE_WITH_SUBLABEL,
  ARC_CAPTION_LAYOUT.subLabelOffsetY + ARC_CAPTION_LAYOUT.subLabelFontSize
)

const captionChartHeight = (radius, ringThickness = RING_THICKNESS) =>
  chartHeight(radius, ARC_CAPTION_CLEARANCE, ringThickness)

export const CHART_HEIGHT_SINGLE_CAPTION = captionChartHeight(PRIMARY_RADIUS)
export const CHART_HEIGHT_DUAL_CAPTION = captionChartHeight(OUTER_RADIUS)

const CALLOUT_FONT_SIZE = 14
const GOAL_CALLOUT_PERCENT = 72 // fixed arc position for the single-mode callout
const CALLOUT_BOX_WIDTH = 140 // wide enough for the non-wrapping amount
const CALLOUT_BOX_HEIGHT = 36

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`

const Chart = styled.svg`
  display: block;
  width: 100%;
  height: auto;

  /* The callout extends outside the viewBox. The doubled selector overrides
     normalize.css's \`svg:not(:root) { overflow: hidden }\` so it is not clipped. */
  && {
    overflow: visible;
  }
`

const arcAt = (outerRadius, toPercent, fromPercent = 0, thickness = RING_THICKNESS) =>
  buildArcPath({
    innerRadius: outerRadius - thickness,
    outerRadius,
    fromPercent,
    toPercent,
    cornerRadius: thickness / 2,
  })

/**
 * Half-donut gauge for retirement income, with the dollar amount at the center.
 *  - single: track + goal band + plan arc. A zero `goalPercent` omits the goal band.
 *  - dual: inner ring = modified case; outer ring = recommended case, marked by a tick.
 *
 * Opt in with `animate` to fill the plan arc in on mount.
 */
const RetirementIncomeDonut = ({
  amount,
  replacementPercent,
  recommendedPercent,
  goalPercent = 80,
  monthlyLabel = 'monthly',
  centerValue,
  subLabel,
  goalCallout,
  colors,
  layout,
  animate = false,
  className,
}) => {
  const theme = useTheme()
  const resolved = resolveColors(theme, colors)
  const geometry = resolveLayout(layout)
  const isDual = recommendedPercent != null

  // Only the plan arc animates; the track, goal band and tick are static.
  const planPercent = useAnimatedPercent(replacementPercent, animate)

  const innerRadius = geometry.radius
  const ringThickness = geometry.ringThickness
  const outerRadius = innerRadius + RING_GAP + ringThickness
  const outerRingInnerRadius = outerRadius - ringThickness
  const chartRadius = isDual ? outerRadius : innerRadius

  const min = -(chartRadius + geometry.padding)
  const width = chartWidth(chartRadius, geometry.padding)

  // Reserve enough bottom space for a custom sub-label position.
  const captionClearance = subLabel
    ? Math.max(
        CAPTION_CLEARANCE_WITH_SUBLABEL,
        geometry.subLabelOffsetY + geometry.subLabelFontSize
      )
    : CAPTION_CLEARANCE

  const height = chartHeight(chartRadius, captionClearance, ringThickness, geometry.padding)

  const formattedAmount = numberToDollars(amount ?? 0, true)
  const ariaLabel = centerValue
    ? `${centerValue} ${monthlyLabel}`
    : `$${formattedAmount} monthly retirement income${subLabel ? `, ${subLabel}` : ''}`

  const tick = isDual
    ? tickMarkPoints({
        percent: recommendedPercent,
        // Offset by half the stroke width so the round cap begins at the
        // outer ring's inner edge.
        fromRadius: outerRingInnerRadius + TICK_WIDTH / 2,
        toRadius: outerRadius + TICK_OUTER_OVERSHOOT,
      })
    : null

  // Dual mode anchors the callout on the recommended tick; single mode uses a
  // fixed position.
  const calloutPercent = isDual ? recommendedPercent : GOAL_CALLOUT_PERCENT
  const calloutRadius =
    (isDual ? outerRadius : innerRadius) + TICK_OUTER_OVERSHOOT + geometry.calloutGap
  const { x: calloutX, y: calloutY } = pointOnGauge(calloutPercent, calloutRadius)

  return (
    <Wrapper className={className}>
      <Chart
        viewBox={`${min} ${min} ${width} ${height}`}
        style={{ maxWidth: `${width}px` }}
        role='img'
        aria-label={ariaLabel}>
        {isDual ? (
          <>
            {/* Outer ring: recommended case. */}
            <path
              d={arcAt(outerRadius, 100 - OUTER_TRACK_TRIM, OUTER_TRACK_TRIM, ringThickness)}
              fill={resolved.track}
            />
            <line
              x1={tick.x1}
              y1={tick.y1}
              x2={tick.x2}
              y2={tick.y2}
              stroke={resolved.tick}
              strokeWidth={TICK_WIDTH}
              strokeLinecap='round'
            />

            {/* Inner ring: modified case, matching the single-mode ring size. */}
            <path d={arcAt(innerRadius, 100, 0, ringThickness)} fill={resolved.track} />
            <path d={arcAt(innerRadius, planPercent, 0, ringThickness)} fill={resolved.plan} />
          </>
        ) : (
          <>
            {/* Single ring: track, goal band, then plan arc. */}
            <path d={arcAt(innerRadius, 100, 0, ringThickness)} fill={resolved.track} />
            {goalPercent > 0 && (
              <path d={arcAt(innerRadius, goalPercent, 0, ringThickness)} fill={resolved.goal} />
            )}
            <path d={arcAt(innerRadius, planPercent, 0, ringThickness)} fill={resolved.plan} />
          </>
        )}

        <text
          x={centerValue ? 0 : AMOUNT_X}
          y={geometry.amountOffsetY}
          textAnchor='middle'
          fontFamily={FONT_FAMILY}
          fontSize={geometry.amountFontSize}
          fontWeight={geometry.amountFontWeight}>
          {centerValue ? (
            <tspan fill={resolved.digits}>{centerValue}</tspan>
          ) : (
            <>
              <tspan fill={resolved.dollarSign}>$</tspan>
              <tspan fill={resolved.digits}>{formattedAmount}</tspan>
            </>
          )}
        </text>

        <text
          x={0}
          y={geometry.monthlyOffsetY}
          textAnchor='middle'
          fontFamily={FONT_FAMILY}
          fontSize={geometry.monthlyFontSize}
          fontWeight={geometry.monthlyFontWeight}
          fill={resolved.monthly}>
          {monthlyLabel}
        </text>

        {subLabel && (
          <text
            x={0}
            y={geometry.subLabelOffsetY}
            textAnchor='middle'
            fontFamily={FONT_FAMILY}
            fontSize={geometry.subLabelFontSize}
            fontWeight={SUB_LABEL_FONT_WEIGHT}
            fill={resolved.subLabel}>
            {subLabel}
          </text>
        )}

        {/* Use HTML so the label and amount lines center independently. */}
        {goalCallout && (
          <foreignObject
            x={calloutX}
            y={calloutY - CALLOUT_BOX_HEIGHT / 2}
            width={CALLOUT_BOX_WIDTH}
            height={CALLOUT_BOX_HEIGHT}
            overflow='visible'>
            <div
              xmlns='http://www.w3.org/1999/xhtml'
              style={{
                display: 'inline-block',
                whiteSpace: 'nowrap',
                textAlign: 'center',
                fontFamily: FONT_FAMILY,
                fontSize: CALLOUT_FONT_SIZE,
                lineHeight: 1.4,
                color: resolved.goalCallout,
              }}>
              <div>{goalCallout.label ?? 'Goal:'}</div>
              <div>
                ${numberToDollars(goalCallout.amount, true)} ({formatPercentLabel(goalCallout.percent)}%)
              </div>
            </div>
          </foreignObject>
        )}
      </Chart>
    </Wrapper>
  )
}

RetirementIncomeDonut.propTypes = {
  /** Dollar amount shown at the center when `centerValue` is not provided. */
  amount: PropTypes.number,
  /** Text shown at the center instead of `amount`, such as a percentage. */
  centerValue: PropTypes.string,
  /** 0–100 value for the plan arc; renders as the inner ring in dual mode. */
  replacementPercent: PropTypes.number.isRequired,
  /** Enables dual mode when set; 0–100 position of the recommended-case tick. */
  recommendedPercent: PropTypes.number,
  /** 0–100 value for the goal band in single mode. */
  goalPercent: PropTypes.number,
  monthlyLabel: PropTypes.string,
  subLabel: PropTypes.string,
  /** Optional goal or recommendation callout beside the chart. */
  goalCallout: PropTypes.shape({
    label: PropTypes.string,
    amount: PropTypes.number.isRequired,
    percent: PropTypes.number.isRequired,
  }),
  /** Optional geometry overrides in viewBox units; see LAYOUT_DEFAULTS. */
  layout: PropTypes.shape({
    radius: PropTypes.number,
    ringThickness: PropTypes.number,
    padding: PropTypes.number,
    amountOffsetY: PropTypes.number,
    amountFontSize: PropTypes.number,
    amountFontWeight: PropTypes.number,
    monthlyOffsetY: PropTypes.number,
    monthlyFontSize: PropTypes.number,
    monthlyFontWeight: PropTypes.number,
    subLabelOffsetY: PropTypes.number,
    subLabelFontSize: PropTypes.number,
    calloutGap: PropTypes.number,
  }),
  /** Optional color overrides. */
  colors: PropTypes.shape({
    track: PropTypes.string,
    goal: PropTypes.string,
    plan: PropTypes.string,
    digits: PropTypes.string,
    dollarSign: PropTypes.string,
    monthly: PropTypes.string,
    subLabel: PropTypes.string,
    goalCallout: PropTypes.string,
    tick: PropTypes.string,
  }),
  /**
   * Fills the plan arc in from the start of the gauge on mount instead of
   * rendering it at its final length. Off by default so only the screens that
   * call for the effect opt in. Ignored when the user prefers reduced motion.
   */
  animate: PropTypes.bool,
  className: PropTypes.string,
}

export default RetirementIncomeDonut
